import React, { useMemo, useState } from 'react';

const StudentBadge = ({ info, onToggleStatus }) => {
  const isPassive = info.status === 0;

  const getBadgeStyle = () => {
    if (isPassive) return 'bg-red-100 text-red-700 border-red-300';
    const dayCount = info.dayCount;
    if (dayCount === null || dayCount === undefined) return 'bg-gray-100 text-gray-600 border-gray-200';
    if (dayCount === 0) return 'bg-green-50 text-green-700 border-green-200';
    if (dayCount === 1) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (dayCount <= 7) return 'bg-blue-50 text-blue-700 border-blue-200';
    if (dayCount <= 30) return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-red-50 text-red-700 border-red-200';
  };

  return (
    <div className="inline-flex items-center gap-2 group">
      <div
        className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border-2 ${getBadgeStyle()}`}
        title={`${info.name} - ${info.phone || 'Tel yok'} - Son ders: ${info.lastLessonInfo} - Durum: ${isPassive ? 'Pasif' : 'Aktif'}`}
      >
        <span className="mr-1">{info.icon}</span>
        <span className="truncate max-w-[120px]">{info.name}</span>
        {isPassive ? (
          <span className="ml-2 px-1.5 py-0.5 bg-red-600 text-white rounded text-[10px] font-bold">P</span>
        ) : (
          <span className="ml-2 px-1.5 py-0.5 bg-green-600 text-white rounded text-[10px] font-bold">A</span>
        )}
        {!isPassive && info.dayCount !== null && info.dayCount !== undefined && (
          <span className="ml-1 opacity-75">({info.lastLessonInfo})</span>
        )}
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          onToggleStatus(info.ogretmenders_id);
        }}
        className={`flex items-center justify-center gap-1 px-2 py-1 rounded-md text-xs font-semibold transition-all shadow-md ${
          isPassive
            ? 'bg-green-600 text-white hover:bg-green-700'
            : 'bg-red-600 text-white hover:bg-red-700'
        }`}
        title={isPassive ? 'Öğrenciyi aktif yap' : 'Öğrenciyi pasif yap'}
      >
        {isPassive ? (
          <>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Aktif Et</span>
          </>
        ) : (
          <>
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span>Pasif Et</span>
          </>
        )}
      </button>
    </div>
  );
};

const TeacherTableRow = ({
  teacher,
  isSelected,
  onSelectChange,
  onEdit,
  onDelete,
  onViewFinancial,
  onViewLessons,
  onLoginAsTeacher,
  onToggleStudentStatus,
  netUcret,
  dersAdlari,
  statusToText,
}) => {
  const [showAllStudents, setShowAllStudents] = useState(false);

  const studentInfos = useMemo(() => {
    if (!teacher.students || teacher.students.length === 0) return [];

    const uniqueStudents = [];
    const seenIds = new Set();

    teacher.students.forEach(student => {
      if (!seenIds.has(student.id)) {
        seenIds.add(student.id);
        uniqueStudents.push(student);
      }
    });

    return uniqueStudents.map(student => {
      const dayCount = student.days_since_last_lesson;

      let info = {
        name: student.name,
        id: student.id,
        ogretmenders_id: student.ogretmenders_id,
        phone: student.parent_phone,
        lastLessonDate: student.last_lesson_date,
        dayCount: dayCount,
        status: student.status !== undefined ? student.status : 1
      };

      if (dayCount === undefined || dayCount === null) {
        Object.assign(info, { lastLessonInfo: 'Ders yok', icon: '—' });
      } else {
        const absDay = Math.abs(dayCount);
        if (absDay === 0) Object.assign(info, { lastLessonInfo: 'Bugün', icon: '✓' });
        else if (absDay === 1) Object.assign(info, { lastLessonInfo: 'Dün', icon: '✓' });
        else if (absDay <= 7) Object.assign(info, { lastLessonInfo: `${absDay} gün`, icon: '→' });
        else if (absDay <= 30) Object.assign(info, { lastLessonInfo: `${absDay} gün`, icon: '⚠' });
        else Object.assign(info, { lastLessonInfo: `${absDay} gün`, icon: '✗' });
      }

      return info;
    });
  }, [teacher.students]);

  const displayedStudents = showAllStudents ? studentInfos : studentInfos.slice(0, 3);

  const getStatusDot = (status) => {
    const text = statusToText(status);
    let dot = 'bg-gray-400';
    if (status === 1 || status === '1') dot = 'bg-emerald-500';
    else if (status === 2 || status === '2') dot = 'bg-amber-400';
    else dot = 'bg-red-400';

    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-gray-700">
        <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />
        {text}
      </span>
    );
  };

  const tdClass = "px-3 py-2 align-top";

  return (
    <tr className={`hover:bg-gray-50 ${isSelected ? 'bg-gray-50' : ''}`}>
      <td className={`sticky left-0 z-10 ${isSelected ? 'bg-gray-50' : 'bg-white'} px-3 py-2`}>
        <input
          type="checkbox"
          className="h-4 w-4 accent-gray-900"
          checked={isSelected}
          onChange={() => onSelectChange(teacher.id)}
        />
      </td>
      <td className={`${tdClass} text-gray-500 text-xs`}>{teacher.id}</td>
      <td className={tdClass}>
        <div className="text-sm font-medium text-gray-900">{teacher.adi}</div>
      </td>
      <td className={`${tdClass} text-gray-600 whitespace-nowrap`}>{teacher.telefon}</td>
      <td className={tdClass}>
        <div className="text-gray-600 truncate max-w-[200px]" title={teacher.e_posta}>
          {teacher.e_posta}
        </div>
      </td>
      <td className={`${tdClass} text-gray-500 font-mono text-xs`}>
        {teacher.psr || <span className="text-gray-300">—</span>}
      </td>
      <td className={tdClass}>
        {dersAdlari.length > 0 ? (
          <div className="flex items-center gap-1.5">
            <span className="text-gray-700">{dersAdlari.join(', ')}</span>
            <button
              className="text-gray-400 hover:text-gray-900 transition-colors"
              onClick={() => onViewLessons(teacher.id, teacher.adi)}
              title="Geçmiş dersler"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>
      <td className={tdClass}>
        {studentInfos.length === 0 ? (
          <span className="text-gray-400 text-sm">Öğrenci yok</span>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {displayedStudents.map((info, index) => (
              <StudentBadge
                key={`${info.id}-${index}`}
                info={info}
                onToggleStatus={(ogretmendersId) => onToggleStudentStatus(teacher.id, ogretmendersId)}
              />
            ))}
            {studentInfos.length > 3 && (
              <button
                onClick={() => setShowAllStudents(!showAllStudents)}
                className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
              >
                {showAllStudents ? `${studentInfos.length - 3} gizle` : `+${studentInfos.length - 3}`}
              </button>
            )}
          </div>
        )}
      </td>
      <td className={tdClass}>
        <button
          onClick={() => onViewFinancial(teacher)}
          className="group inline-flex items-start gap-1 text-left hover:text-gray-900"
          title="Finansal rapor"
        >
          <div>
            <div className={`font-medium ${netUcret > 0 ? 'text-gray-900' : netUcret < 0 ? 'text-red-600' : 'text-gray-400'}`}>
              ₺{Math.abs(netUcret).toLocaleString('tr-TR')}
            </div>
            {teacher.financialDetails && teacher.financialDetails.toplamAvans !== 0 && (
              <div className={`text-xs ${
                teacher.financialDetails.toplamAvans < 0 ? 'text-red-500' : 'text-amber-600'
              }`}>
                Avans ₺{Math.abs(teacher.financialDetails.toplamAvans).toLocaleString('tr-TR')}
              </div>
            )}
          </div>
          <svg className="w-3 h-3 mt-1 text-gray-300 group-hover:text-gray-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </td>
      <td className={tdClass}>{getStatusDot(teacher.status)}</td>
      <td className={`${tdClass} text-gray-500 text-xs whitespace-nowrap`}>
        {teacher.created_at ? new Date(teacher.created_at).toLocaleDateString('tr-TR') : '—'}
      </td>
      <td className={`${tdClass} whitespace-nowrap`}>
        <div className="flex items-center justify-end gap-0.5">
          <button
            className="p-1.5 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            onClick={() => onEdit(teacher)}
            title="Düzenle"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            className="p-1.5 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            onClick={() => onLoginAsTeacher(teacher.id)}
            title="Öğretmen paneline giriş"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
            </svg>
          </button>
          <button
            className="p-1.5 rounded text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors"
            onClick={() => onDelete(teacher.id)}
            title="Sil"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </td>
    </tr>
  );
};

export default TeacherTableRow;
